import { DRAWER_BODY_RESET } from './ui/drawerOverrides';
import React, { useState, useEffect } from 'react';
import { Drawer, SIZE, ANCHOR } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Checkbox } from 'baseui/checkbox';
import { styled } from 'baseui';
import { HeadingSmall, LabelSmall, ParagraphSmall } from 'baseui/typography';
import { Shield, Trash2, Save, X } from 'lucide-react';
import { useCreateUserType, useUpdateUserType, useDeleteUserType } from '../hooks/useUserTypes';
import type { UserType } from '../types/role.types';

const DrawerContent = styled('div', {
  padding: '0',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const DrawerHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '24px 24px 20px',
  borderBottom: '1px solid #F3F4F6',
});

const AvatarCircle = styled('div', {
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  backgroundColor: 'var(--tenant-primary, #111827)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const DrawerBody = styled('div', {
  flex: 1,
  overflowY: 'auto',
  padding: '20px 24px',
});

const Section = styled('div', {
  marginBottom: '24px',
});

const SectionTitle = styled('div', {
  fontSize: '12px',
  fontWeight: 600,
  color: '#9CA3AF',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  marginBottom: '12px',
});

const DrawerFooter = styled('div', {
  padding: '16px 24px',
  borderTop: '1px solid #F3F4F6',
  display: 'flex',
  gap: '12px',
  justifyContent: 'space-between',
});

const ActionBtn = styled('button', {
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#F9FAFB',
  color: '#374151',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: '#F3F4F6' },
  ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

const DangerActionBtn = styled('button', {
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  color: '#DC2626',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: '#FEF2F2' },
  ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

const PrimaryBtn = styled('button', {
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
  ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

interface UserTypeDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userType: UserType | null;
  mode: 'create' | 'edit';
}

export const UserTypeDetailDrawer: React.FC<UserTypeDetailDrawerProps> = ({
  isOpen,
  onClose,
  userType,
  mode,
}) => {
  const createUserType = useCreateUserType();
  const updateUserType = useUpdateUserType();
  const deleteUserType = useDeleteUserType();

  const [formData, setFormData] = useState({
    name: '',
    is_register: false,
    is_new_rol_request: false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && userType) {
      setFormData({
        name: userType.name || '',
        is_register: userType.is_register ?? false,
        is_new_rol_request: userType.is_new_rol_request ?? false,
      });
    } else {
      setFormData({
        name: '',
        is_register: false,
        is_new_rol_request: false,
      });
    }
    setShowDeleteConfirm(false);
  }, [userType, mode, isOpen]);

  const handleSave = () => {
    const data: Partial<UserType> = {
      name: formData.name,
      is_register: formData.is_register,
      is_new_rol_request: formData.is_new_rol_request,
    };

    if (mode === 'create') {
      createUserType.mutate(data, { onSuccess: () => onClose() });
    } else if (userType) {
      updateUserType.mutate({ id: userType.id, data }, { onSuccess: () => onClose() });
    }
  };

  const handleDelete = () => {
    if (!userType) return;
    deleteUserType.mutate(userType.id, { onSuccess: () => onClose() });
  };

  const isSaving = createUserType.isPending || updateUserType.isPending;
  const isDeleting = deleteUserType.isPending;
  const isFormValid = formData.name.trim().length > 0;
  const title = mode === 'create' ? 'New Role' : userType?.name || 'Role';

  return (
    <Drawer
      isOpen={isOpen}
      autoFocus
      onClose={onClose}
      size={SIZE.default}
      anchor={ANCHOR.right}
      overrides={{
        Root: { style: { zIndex: 9999 } },
        DrawerContainer: { style: { width: '480px', zIndex: 10000 } },
        DrawerBody: { style: { ...DRAWER_BODY_RESET } },
        Close: { style: { display: 'none' } },
      }}
    >
      <DrawerContent>
        <DrawerHeader>
          <AvatarCircle>
            <Shield size={20} color="#fff" />
          </AvatarCircle>
          <div>
            <HeadingSmall margin={0} style={{ fontSize: '18px', fontWeight: 600 }}>{title}</HeadingSmall>
            <LabelSmall color="#6B7280" marginTop="2px">
              {mode === 'create' ? 'Create a new role' : `ID: ${userType?.id}`}
            </LabelSmall>
          </div>
        </DrawerHeader>

        <DrawerBody>
          <Section>
            <SectionTitle>Role Information</SectionTitle>

            <FormControl label="Name">
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: (e.target as HTMLInputElement).value }))}
                placeholder="Role name"
                size="compact"
              />
            </FormControl>

            <FormControl label="">
              <Checkbox
                checked={formData.is_register}
                onChange={(e) => setFormData((p) => ({ ...p, is_register: (e.target as HTMLInputElement).checked }))}
              >
                Available in registration
              </Checkbox>
            </FormControl>

            <FormControl label="">
              <Checkbox
                checked={formData.is_new_rol_request}
                onChange={(e) => setFormData((p) => ({ ...p, is_new_rol_request: (e.target as HTMLInputElement).checked }))}
              >
                Requires estate selection
              </Checkbox>
            </FormControl>
          </Section>

          {mode === 'edit' && userType && (
            <Section>
              <SectionTitle>Danger Zone</SectionTitle>
              {!showDeleteConfirm ? (
                <DangerActionBtn
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  style={{ width: '100%' }}
                >
                  <Trash2 size={14} />
                  Delete Role
                </DangerActionBtn>
              ) : (
                <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  <ParagraphSmall margin="0 0 12px" color="#374151" style={{ fontSize: '13px', fontWeight: 500 }}>
                    This action is permanent and cannot be undone.
                  </ParagraphSmall>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionBtn
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </ActionBtn>
                    <DangerActionBtn
                      onClick={handleDelete}
                      disabled={isDeleting}
                      style={{ flex: 1 }}
                    >
                      <Trash2 size={14} />
                      {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                    </DangerActionBtn>
                  </div>
                </div>
              )}
            </Section>
          )}
        </DrawerBody>

        <DrawerFooter>
          <ActionBtn onClick={onClose} disabled={isSaving}>
            <X size={14} />
            Cancel
          </ActionBtn>
          <PrimaryBtn onClick={handleSave} disabled={!isFormValid || isSaving}>
            <Save size={14} />
            {isSaving ? 'Saving...' : mode === 'create' ? 'Create Role' : 'Save Changes'}
          </PrimaryBtn>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
